import type { ChatbotUsageStatus, ChatbotUsageWindowStatus } from "../../services/chatbot.service";
import {
  USAGE_LIMIT_REACHED_TRANSLATION_KEY,
  USAGE_LIMIT_RESET_LINE_TRANSLATION_KEY,
  USAGE_LIMIT_RESETS_INTRO_TRANSLATION_KEY,
  USAGE_LIMIT_WINDOW_PREFIX_TRANSLATION_KEY,
} from "./chatbotHook.constants";

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const isWindowLimitReached = (window: ChatbotUsageWindowStatus): boolean => {
  const tokenLimitReached = window.token_limit > 0 && window.tokens_used >= window.token_limit;
  const imageLimitReached =
    window.image_generation_limit > 0 &&
    window.image_generations_used >= window.image_generation_limit;
  const costLimitReached = window.cost_limit_usd > 0 && window.cost_usd >= window.cost_limit_usd;

  return tokenLimitReached || imageLimitReached || costLimitReached;
};

export const buildUsageLimitMessage = (
  usageStatus: ChatbotUsageStatus,
  t: Translate
): string => {
  const exhaustedWindows = usageStatus.usage_windows.filter(isWindowLimitReached);

  if (!exhaustedWindows.length) {
    return t(USAGE_LIMIT_REACHED_TRANSLATION_KEY);
  }

  const formatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const resetLines = exhaustedWindows.flatMap((window) => {
    if (!window.reset_at) return [];

    const windowLabel = t(`${USAGE_LIMIT_WINDOW_PREFIX_TRANSLATION_KEY}_${window.key}`);
    const resetAt = formatter.format(new Date(window.reset_at));

    return [
      t(USAGE_LIMIT_RESET_LINE_TRANSLATION_KEY, {
        window: windowLabel,
        resetAt,
        interpolation: { escapeValue: false },
      }),
    ];
  });

  return [
    t(USAGE_LIMIT_REACHED_TRANSLATION_KEY),
    ...(resetLines.length
      ? ["", t(USAGE_LIMIT_RESETS_INTRO_TRANSLATION_KEY), ...resetLines.map((line) => `- ${line}`)]
      : []),
  ].join("\n");
};
